import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Download, Heart, MessageCircle, Play, Send, Share2, ShoppingBag,
  Volume2, VolumeX, X,
} from "lucide-react";
import { api, mediaUrl, sizedMediaUrl } from "../lib/api";
import { pickLocale } from "../lib/format";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { adfAssets } from "../components/storefront/assets";

/**
 * Qalereya — iki qat:
 *   1) Kart görünüşü: Instagram profil səhifəsi kimi — avatar, handle, İzlə,
 *      tablar və reels plitələri (saytın öz header/footer-i ilə).
 *   2) Plitəyə klik → tam ekran TikTok/Reels feed-i (swipe, like, rəy,
 *      paylaş, endir, məhsula keçid). Geri oxu karta qaytarır.
 */
const INSTAGRAM_URL = "https://www.instagram.com/atelierdifalco.azerbaijan";
const INSTAGRAM_HANDLE = "atelierdifalco.azerbaijan";

function InstagramGlyph({ size = 15 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="2.6" y="2.6" width="18.8" height="18.8" rx="5.4" />
      <circle cx="12" cy="12" r="4.4" />
      <circle cx="17.6" cy="6.4" r="1.15" fill="currentColor" stroke="none" />
    </svg>
  );
}

export default function Gallery() {
  const { locale, t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [kind, setKind] = useState("all");
  const [openIndex, setOpenIndex] = useState(-1); // -1 = kart görünüşü
  const [activeIndex, setActiveIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const [commentsFor, setCommentsFor] = useState(null);
  const [toast, setToast] = useState(null); // {text, action?}
  const feedRef = useRef(null);
  const jumpedRef = useRef(false);

  useEffect(() => {
    api.get("/gallery", { params: { limit: 120 } })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  }, [user?.id]);

  useEffect(() => { document.title = `${t("gallery.title", "Qalereya")} | Atelier Di Falco`; }, [t]);

  const visible = useMemo(
    () => (items || []).filter((item) => (kind === "all" ? true : item.media_type === kind)),
    [items, kind],
  );

  /* Paylaşılan link (?item=<id>) birbaşa feed-i həmin elementdə açır. */
  useEffect(() => {
    if (jumpedRef.current || !visible.length) return;
    const target = params.get("item");
    jumpedRef.current = true;
    if (!target) return;
    const index = visible.findIndex((item) => item.id === target);
    if (index >= 0) setOpenIndex(index);
  }, [visible, params]);

  /* Feed açılanda seçilmiş elementə sürüş və arxa səhifəni kilidlə. */
  useEffect(() => {
    if (openIndex < 0) return undefined;
    setActiveIndex(openIndex);
    requestAnimationFrame(() => {
      feedRef.current?.children[openIndex]?.scrollIntoView({ behavior: "instant", block: "start" });
    });
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [openIndex]);

  /* Hansı slayd ekrandadır — video idarəsi üçün. */
  useEffect(() => {
    if (openIndex < 0) return undefined;
    const root = feedRef.current;
    if (!root) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            setActiveIndex(Number(entry.target.dataset.index || 0));
          }
        }
      },
      { root, threshold: [0.6] },
    );
    Array.from(root.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [openIndex, visible.length]);

  const flash = useCallback((text, action) => {
    setToast({ text, action });
    window.setTimeout(() => setToast(null), 2600);
  }, []);

  const goLogin = useCallback(() => {
    navigate(`/auth/login?next=${encodeURIComponent("/gallery")}`);
  }, [navigate]);

  const toggleLike = useCallback(async (item) => {
    if (!user) {
      flash(t("gallery.login_to_like", "Bəyənmək üçün daxil olun"), goLogin);
      return;
    }
    const wasLiked = item.liked;
    setItems((current) => (current || []).map((it) => it.id === item.id
      ? { ...it, liked: !wasLiked, like_count: Math.max(0, (it.like_count || 0) + (wasLiked ? -1 : 1)) }
      : it));
    try {
      const r = wasLiked
        ? await api.delete(`/gallery/${item.id}/like`)
        : await api.post(`/gallery/${item.id}/like`);
      setItems((current) => (current || []).map((it) => it.id === item.id
        ? { ...it, liked: r.data.liked, like_count: r.data.like_count }
        : it));
    } catch {
      setItems((current) => (current || []).map((it) => it.id === item.id
        ? { ...it, liked: wasLiked, like_count: item.like_count }
        : it));
    }
  }, [user, flash, goLogin, t]);

  const share = useCallback(async (item) => {
    const url = `${window.location.origin}/gallery?item=${item.id}`;
    const title = pickLocale(item.caption, locale) || "Atelier Di Falco";
    if (navigator.share) {
      try { await navigator.share({ title, url }); return; } catch { /* imtina */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      flash(t("gallery.link_copied", "Link kopyalandı"));
    } catch {
      flash(url);
    }
  }, [locale, t, flash]);

  const closeFeed = useCallback(() => {
    setOpenIndex(-1);
    setCommentsFor(null);
    if (params.get("item")) setParams({}, { replace: true });
  }, [params, setParams]);

  const tabs = [
    ["all", t("gallery.tab_all", "Hamısı")],
    ["video", t("gallery.tab_videos", "Video")],
    ["image", t("gallery.tab_photos", "Şəkil")],
  ];

  return (
    <div className="bg-ivory text-graphite" data-testid="gallery-page">
      <Header />
      <main id="main">
        {/* Instagram profil başlığı — solda, grid ilə eyni hizada */}
        <section className="adf-gprofile">
          <span className="adf-gprofile__avatar" aria-hidden="true">
            <img src={adfAssets.logoMono} alt="" />
          </span>
          <div className="adf-gprofile__info">
            <div className="adf-gprofile__row">
              <span className="adf-gprofile__handle">
                <InstagramGlyph size={20} /> {INSTAGRAM_HANDLE}
              </span>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noreferrer"
                className="adf-gprofile__follow"
                data-testid="gallery-instagram"
              >
                {t("gallery.follow", "İzlə")}
              </a>
            </div>
            <p className="adf-gprofile__bio">
              Atelier Di Falco — {t("gallery.default_caption", "keyfiyyət incəsənəti")}.
            </p>
          </div>
        </section>

        <div className="adf-gallery-tabs" role="tablist" aria-label={t("gallery.title", "Qalereya")}>
          {tabs.map(([key, tabLabel]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={kind === key}
              className={kind === key ? "is-active" : ""}
              onClick={() => { setKind(key); }}
            >
              {tabLabel}
            </button>
          ))}
        </div>

        {items && visible.length === 0 && (
          <p className="adf-container pb-24 text-center text-[var(--adf-gray-2)]">
            {t("gallery.empty", "Qalereya hazırlanır — tezliklə burada olacaq.")}
          </p>
        )}

        {/* Kart görünüşü — reels plitələri */}
        <div className="adf-gallery-grid" data-testid="gallery-grid">
          {visible.map((item, index) => (
            <button
              key={item.id}
              type="button"
              className="adf-gallery-tile"
              onClick={() => setOpenIndex(index)}
              aria-label={pickLocale(item.caption, locale) || t("gallery.item", "Qalereya elementi")}
            >
              {item.media_type === "video" ? (
                <>
                  <video
                    src={mediaUrl(item.media_url)}
                    poster={item.poster_url ? sizedMediaUrl(item.poster_url, 720) : undefined}
                    muted
                    loop
                    playsInline
                    preload={item.poster_url ? "none" : "metadata"}
                    onMouseEnter={(e) => { e.currentTarget.play().catch(() => {}); }}
                    onMouseLeave={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                  />
                  <span className="adf-gallery-play" aria-hidden="true"><Play size={15} fill="currentColor" /></span>
                </>
              ) : (
                <img src={sizedMediaUrl(item.media_url, 720)} alt="" loading={index > 7 ? "lazy" : undefined} />
              )}
              <span className="adf-gallery-tile__stats" aria-hidden="true">
                <Heart size={13} fill="currentColor" /> {item.like_count || 0}
                <MessageCircle size={13} className="ms-1" /> {item.comment_count || 0}
              </span>
            </button>
          ))}
        </div>
      </main>
      <Footer />

      {/* Tam ekran feed — plitəyə klikdə açılır */}
      {openIndex >= 0 && (
        <div className="adf-feed-page" data-testid="gallery-feed-overlay">
          <div className="adf-feed-top">
            <button type="button" className="adf-feed-back" onClick={closeFeed} aria-label={t("gallery.close", "Bağla")}>
              <ArrowLeft size={20} strokeWidth={1.6} />
            </button>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="adf-feed-profile">
              <span className="adf-feed-profile__ring"><InstagramGlyph /></span>
              <span className="adf-feed-profile__name">{INSTAGRAM_HANDLE}</span>
              <span className="adf-feed-profile__follow">{t("gallery.follow", "İzlə")}</span>
            </a>
          </div>

          <div className="adf-feed" ref={feedRef} data-testid="gallery-feed">
            {visible.map((item, index) => (
              <FeedSlide
                key={item.id}
                item={item}
                index={index}
                active={index === activeIndex}
                muted={muted}
                onToggleMute={() => setMuted((m) => !m)}
                onLike={() => toggleLike(item)}
                onComments={() => setCommentsFor(item.id)}
                onShare={() => share(item)}
                locale={locale}
                t={t}
              />
            ))}
          </div>
        </div>
      )}

      {toast && (
        <button
          type="button"
          className="adf-feed-toast"
          onClick={() => { if (toast.action) toast.action(); setToast(null); }}
        >
          {toast.text}{toast.action ? " →" : ""}
        </button>
      )}

      {commentsFor && (
        <CommentsSheet
          item={(items || []).find((it) => it.id === commentsFor)}
          onClose={() => setCommentsFor(null)}
          onCountChange={(id, delta) => setItems((current) => (current || []).map((it) =>
            it.id === id ? { ...it, comment_count: Math.max(0, (it.comment_count || 0) + delta) } : it))}
          user={user}
          onLogin={goLogin}
          t={t}
        />
      )}
    </div>
  );
}

function FeedSlide({ item, index, active, muted, onToggleMute, onLike, onComments, onShare, locale, t }) {
  const videoRef = useRef(null);
  const caption = pickLocale(item.caption, locale)
    || `Atelier Di Falco — ${t("gallery.default_caption", "keyfiyyət incəsənəti")}`;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (active) {
      video.muted = muted;
      video.play().catch(() => { video.muted = true; video.play().catch(() => {}); });
    } else {
      video.pause();
      video.currentTime = 0;
    }
  }, [active, muted]);

  const [downloading, setDownloading] = useState(false);
  const downloadHref = `${mediaUrl(item.media_url)}${item.media_url.includes("?") ? "&" : "?"}download=1`;

  /* Fayl birbaşa cihaza enir — blob ilə: brauzer heç bir halda api
     domeninə keçmir. CORS media endpointində açıqdır. */
  const downloadItem = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const response = await fetch(downloadHref);
      if (!response.ok) throw new Error("download failed");
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = decodeURIComponent(item.media_url.split("/").pop() || "atelier-media");
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
    } catch {
      window.open(downloadHref, "_blank", "noopener");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section className="adf-feed-slide" data-index={index}>
      {item.media_type === "video" ? (
        <video
          ref={videoRef}
          src={mediaUrl(item.media_url)}
          poster={item.poster_url ? sizedMediaUrl(item.poster_url, 1080) : undefined}
          loop
          playsInline
          muted={muted}
          preload={active ? "auto" : "none"}
          onClick={(e) => { const v = e.currentTarget; if (v.paused) v.play(); else v.pause(); }}
        />
      ) : (
        <img
          src={sizedMediaUrl(item.media_url, 1080)}
          alt={caption}
          loading={index > 2 ? "lazy" : undefined}
        />
      )}

      <div className="adf-feed-shade" aria-hidden="true" />
      <div className="adf-feed-meta">
        {/* Instagram Reels sayağı müəllif bloku: profil şəkli + ad, altında başlıq */}
        <div className="adf-feed-author">
          <span className="adf-feed-author__pic" aria-hidden="true">
            <img src={adfAssets.logoMonoCream || adfAssets.logoMono} alt="" />
          </span>
          <span className="adf-feed-author__name">{INSTAGRAM_HANDLE}</span>
        </div>
        <p className="adf-feed-caption">{caption}</p>
        {item.link_url && (
          <Link to={item.link_url} className="adf-feed-product" data-testid="feed-view-product">
            <ShoppingBag size={15} aria-hidden="true" />
            {t("gallery.view_product", "Məhsula bax")}
          </Link>
        )}
      </div>

      <div className="adf-feed-rail">
        {item.media_type === "video" && (
          <button type="button" onClick={onToggleMute} aria-label={muted ? t("gallery.unmute", "Səsi aç") : t("gallery.mute", "Səsi bağla")}>
            {muted ? <VolumeX size={24} strokeWidth={1.6} /> : <Volume2 size={24} strokeWidth={1.6} />}
          </button>
        )}
        <button
          type="button"
          onClick={onLike}
          className={item.liked ? "is-liked" : ""}
          aria-pressed={item.liked}
          aria-label={t("gallery.like", "Bəyən")}
          data-testid="feed-like"
        >
          <Heart size={25} strokeWidth={1.6} fill={item.liked ? "currentColor" : "none"} />
          <span>{item.like_count || 0}</span>
        </button>
        <button type="button" onClick={onComments} aria-label={t("gallery.comments", "Rəylər")} data-testid="feed-comments">
          <MessageCircle size={25} strokeWidth={1.6} />
          <span>{item.comment_count || 0}</span>
        </button>
        <button type="button" onClick={onShare} aria-label={t("gallery.share", "Paylaş")} data-testid="feed-share">
          <Share2 size={24} strokeWidth={1.6} />
        </button>
        <button
          type="button"
          onClick={downloadItem}
          aria-label={t("gallery.download", "Endir")}
          data-testid="feed-download"
          style={downloading ? { opacity: 0.5 } : undefined}
        >
          <Download size={24} strokeWidth={1.6} />
        </button>
      </div>
    </section>
  );
}

function CommentsSheet({ item, onClose, onCountChange, user, onLogin, t }) {
  const [comments, setComments] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);

  const itemId = item?.id;
  useEffect(() => {
    if (!itemId) return;
    api.get(`/gallery/${itemId}/comments`)
      .then((r) => setComments(r.data?.items || []))
      .catch(() => setComments([]));
  }, [itemId]);

  if (!item) return null;

  const submit = async (event) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const r = await api.post(`/gallery/${item.id}/comments`, { body });
      setComments((current) => [r.data, ...(current || [])]);
      onCountChange(item.id, 1);
      setDraft("");
    } finally {
      setSending(false);
    }
  };

  const removeComment = async (comment) => {
    await api.delete(`/gallery/comments/${comment.id}`);
    setComments((current) => (current || []).filter((c) => c.id !== comment.id));
    onCountChange(item.id, -1);
  };

  return (
    <div className="adf-feed-comments" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="adf-feed-comments__sheet" onClick={(e) => e.stopPropagation()}>
        <div className="adf-feed-comments__head">
          <span>{t("gallery.comments", "Rəylər")} · {comments?.length ?? "…"}</span>
          <button type="button" onClick={onClose} aria-label={t("gallery.close", "Bağla")}><X size={20} /></button>
        </div>
        <div className="adf-feed-comments__list">
          {comments?.length === 0 && (
            <p className="adf-feed-comments__empty">{t("gallery.no_comments", "İlk rəyi siz yazın.")}</p>
          )}
          {(comments || []).map((comment) => (
            <div key={comment.id} className="adf-feed-comment">
              <div>
                <span className="adf-feed-comment__author">{comment.author}</span>
                <p>{comment.body}</p>
              </div>
              {comment.mine && (
                <button type="button" onClick={() => removeComment(comment)} aria-label={t("gallery.delete", "Sil")}>
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
        {user ? (
          <form className="adf-feed-comments__form" onSubmit={submit}>
            <input
              value={draft}
              maxLength={500}
              placeholder={t("gallery.comment_placeholder", "Rəy yazın…")}
              onChange={(e) => setDraft(e.target.value)}
            />
            <button type="submit" disabled={!draft.trim() || sending} aria-label={t("gallery.send", "Göndər")}>
              <Send size={18} />
            </button>
          </form>
        ) : (
          <button type="button" className="adf-feed-comments__login" onClick={onLogin}>
            {t("gallery.login_to_comment", "Rəy yazmaq üçün daxil olun")}
          </button>
        )}
      </div>
    </div>
  );
}
